import React, { useState, useEffect, useCallback } from 'react';
import { X, Settings } from 'lucide-react';
import { realSignalApi, UserPreferences } from '@/services/realsignal-api';
import { colors, SettingsSection } from '@/components/RealSignal/SettingsComponents';
import {
    navItems,
    AccountSection,
    NotificationsSection,
    MapSection,
    BillingSection,
} from '@/components/RealSignal/SettingsSections';

interface SettingsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    userEmail?: string;
    userName?: string;
    subscriptionStatus?: 'active' | 'trial' | 'expired';
    subscriptionEndDate?: string;
}

export const SettingsDrawer: React.FC<SettingsDrawerProps> = ({
    isOpen,
    onClose,
    userEmail = 'user@example.com',
    userName = 'RealSignal User',
    subscriptionStatus = 'active',
    subscriptionEndDate = '2026-03-15',
}) => {
    const [activeSection, setActiveSection] = useState<SettingsSection>('account');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [authError, setAuthError] = useState(false);

    const [emailAlerts, setEmailAlerts] = useState(true);
    const [auctionReminders, setAuctionReminders] = useState(true);
    const [priceDropAlerts, setPriceDropAlerts] = useState(true);
    const [weeklyDigest, setWeeklyDigest] = useState(false);

    const [showApproximateLocations, setShowApproximateLocations] = useState(false);
    const [autoFitBounds, setAutoFitBounds] = useState(true);
    const [showCountyLabels, setShowCountyLabels] = useState(true);
    const [isLoadingPortal, setIsLoadingPortal] = useState(false);

    const handleManageBilling = useCallback(async () => {
        setIsLoadingPortal(true);
        try {
            const result = await realSignalApi.createBillingPortalSession();
            if (result.state === 'success' && result.data?.portalUrl) {
                window.location.href = result.data.portalUrl;
            } else if (result.state === 'authError') {
                setAuthError(true);
            } else {
                console.error('[SettingsDrawer] Failed to create portal session:', result.error);
                alert(result.error || 'Failed to open billing portal');
            }
        } catch (err) {
            console.error('[SettingsDrawer] Billing portal error:', err);
            alert('Failed to open billing portal. Please try again.');
        } finally {
            setIsLoadingPortal(false);
        }
    }, []);

    const handleRenewSubscription = useCallback(async () => {
        await handleManageBilling();
    }, [handleManageBilling]);

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setAuthError(false);
            realSignalApi
                .getPreferences()
                .then(result => {
                    if (result.state === 'success' && result.data?.data) {
                        const prefs = result.data.data;
                        setEmailAlerts(prefs.notifications.emailAlerts);
                        setAuctionReminders(prefs.notifications.auctionReminders);
                        setPriceDropAlerts(prefs.notifications.priceDropAlerts);
                        setWeeklyDigest(prefs.notifications.weeklyDigest);
                        setShowApproximateLocations(prefs.mapPreferences.showApproximateLocations);
                        setAutoFitBounds(prefs.mapPreferences.autoFitBounds);
                        setShowCountyLabels(prefs.mapPreferences.showCountyLabels);
                    } else if (result.state === 'authError') {
                        setAuthError(true);
                        console.warn('[SettingsDrawer] Auth required for preferences');
                    }
                })
                .catch(err => {
                    console.error('[SettingsDrawer] Failed to load preferences:', err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [isOpen]);

    const savePreferences = useCallback(
        async (
            updates: Partial<{
                notifications: Partial<UserPreferences['notifications']>;
                mapPreferences: Partial<UserPreferences['mapPreferences']>;
            }>
        ) => {
            setIsSaving(true);
            try {
                const result = await realSignalApi.updatePreferences(updates);
                if (result.state === 'authError') {
                    setAuthError(true);
                }
            } catch (err) {
                console.error('[SettingsDrawer] Failed to save preferences:', err);
            } finally {
                setIsSaving(false);
            }
        },
        []
    );

    const handleEmailAlertsToggle = () => {
        const newValue = !emailAlerts;
        setEmailAlerts(newValue);
        savePreferences({ notifications: { emailAlerts: newValue } });
    };

    const handleAuctionRemindersToggle = () => {
        const newValue = !auctionReminders;
        setAuctionReminders(newValue);
        savePreferences({ notifications: { auctionReminders: newValue } });
    };

    const handlePriceDropAlertsToggle = () => {
        const newValue = !priceDropAlerts;
        setPriceDropAlerts(newValue);
        savePreferences({ notifications: { priceDropAlerts: newValue } });
    };

    const handleWeeklyDigestToggle = () => {
        const newValue = !weeklyDigest;
        setWeeklyDigest(newValue);
        savePreferences({ notifications: { weeklyDigest: newValue } });
    };

    const handleShowApproximateLocationsToggle = () => {
        const newValue = !showApproximateLocations;
        setShowApproximateLocations(newValue);
        savePreferences({ mapPreferences: { showApproximateLocations: newValue } });
    };

    const handleAutoFitBoundsToggle = () => {
        const newValue = !autoFitBounds;
        setAutoFitBounds(newValue);
        savePreferences({ mapPreferences: { autoFitBounds: newValue } });
    };

    const handleShowCountyLabelsToggle = () => {
        const newValue = !showCountyLabels;
        setShowCountyLabels(newValue);
        savePreferences({ mapPreferences: { showCountyLabels: newValue } });
    };

    if (!isOpen) return null;

    const renderContent = () => {
        switch (activeSection) {
            case 'account':
                return (
                    <AccountSection
                        userName={userName}
                        userEmail={userEmail}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                    />
                );
            case 'notifications':
                return (
                    <NotificationsSection
                        isLoading={isLoading}
                        isSaving={isSaving}
                        emailAlerts={emailAlerts}
                        auctionReminders={auctionReminders}
                        priceDropAlerts={priceDropAlerts}
                        weeklyDigest={weeklyDigest}
                        onEmailAlertsToggle={handleEmailAlertsToggle}
                        onAuctionRemindersToggle={handleAuctionRemindersToggle}
                        onPriceDropAlertsToggle={handlePriceDropAlertsToggle}
                        onWeeklyDigestToggle={handleWeeklyDigestToggle}
                    />
                );
            case 'map':
                return (
                    <MapSection
                        isLoading={isLoading}
                        isSaving={isSaving}
                        showApproximateLocations={showApproximateLocations}
                        autoFitBounds={autoFitBounds}
                        showCountyLabels={showCountyLabels}
                        onShowApproximateLocationsToggle={handleShowApproximateLocationsToggle}
                        onAutoFitBoundsToggle={handleAutoFitBoundsToggle}
                        onShowCountyLabelsToggle={handleShowCountyLabelsToggle}
                    />
                );
            case 'billing':
                return (
                    <BillingSection
                        subscriptionStatus={subscriptionStatus}
                        subscriptionEndDate={subscriptionEndDate}
                        onManageBilling={handleManageBilling}
                        onRenewSubscription={handleRenewSubscription}
                        isLoadingPortal={isLoadingPortal}
                    />
                );
            default:
                return (
                    <AccountSection
                        userName={userName}
                        userEmail={userEmail}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                    />
                );
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 999,
                    transition: 'opacity 0.2s ease',
                }}
            />

            {/* Drawer */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '480px',
                    maxWidth: '100vw',
                    height: '100vh',
                    background: colors.background,
                    borderLeft: `1px solid ${colors.border}`,
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideIn 0.2s ease',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem 1.5rem',
                        borderBottom: `1px solid ${colors.border}`,
                        background: colors.backgroundLight,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Settings size={20} style={{ color: colors.accent }} />
                        <h2
                            style={{
                                margin: 0,
                                fontSize: '1.125rem',
                                fontWeight: 600,
                                color: colors.textPrimary,
                            }}
                        >
                            Settings
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: colors.textMuted,
                            cursor: 'pointer',
                            padding: '0.5rem',
                            borderRadius: '0.375rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Navigation */}
                <div
                    style={{
                        display: 'flex',
                        borderBottom: `1px solid ${colors.border}`,
                        background: colors.backgroundLight,
                        overflowX: 'auto',
                    }}
                >
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                padding: '0.875rem 1rem',
                                background: 'transparent',
                                border: 'none',
                                borderBottom:
                                    activeSection === item.id
                                        ? `2px solid ${colors.accent}`
                                        : '2px solid transparent',
                                color:
                                    activeSection === item.id
                                        ? colors.textPrimary
                                        : colors.textMuted,
                                fontSize: '0.8125rem',
                                fontWeight: activeSection === item.id ? 500 : 400,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.icon}
                            {item.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                    }}
                >
                    {renderContent()}
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '1rem 1.5rem',
                        borderTop: `1px solid ${colors.border}`,
                        background: colors.backgroundLight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                        RealSignal v1.0
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <a
                            href="#"
                            style={{
                                color: colors.textMuted,
                                fontSize: '0.75rem',
                                textDecoration: 'none',
                            }}
                        >
                            Privacy Policy
                        </a>
                        <a
                            href="#"
                            style={{
                                color: colors.textMuted,
                                fontSize: '0.75rem',
                                textDecoration: 'none',
                            }}
                        >
                            Terms of Service
                        </a>
                    </div>
                </div>
            </div>

            <style>
                {`
                    @keyframes slideIn {
                        from {
                            transform: translateX(100%);
                        }
                        to {
                            transform: translateX(0);
                        }
                    }
                `}
            </style>
        </>
    );
};

export default SettingsDrawer;
